import { FetchingButton } from '@glade-ai/ui';
import { m } from 'motion/react';
import { type FC, useContext, useEffect, useMemo, useState } from 'react';

import Search from '@/components/DesignLibrary/Atoms/Search';
import ListCell from '@/components/DesignLibrary/ListCell';
import Spacer from '@/components/Spacer';
import { ToastTypeVariants } from '@/context/ToastContext';
import { useToast } from '@/hooks';
import { type WorkflowCollaborator } from '@/hooks/useUserWorkflowContext';
import teamsContext from '@/providers/Teams/TeamsContext';
import type * as ApiModels from '@/typings/api-models';

import s from './CreditReportModal.module.scss';

type Props = {
  onCustomerSelect: (newCustomer: Pick<ApiModels.Person, 'id' | 'name' | 'email' | 'contactPhoneNumber'>) => void;
  selectedCustomer?: Pick<ApiModels.Person, 'id' | 'name' | 'email' | 'contactPhoneNumber'> | null;
  nextButtonText?: string;
  customers: WorkflowCollaborator[];
  isSubmitting?: boolean;
};

const SelectCustomer: FC<Props> = ({
  onCustomerSelect,
  selectedCustomer: existingCustomer,
  nextButtonText = 'Next',
  customers,
  isSubmitting = false,
}) => {
  const [search, setSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Pick<
    ApiModels.Person,
    'id' | 'name' | 'email' | 'contactPhoneNumber'
  > | null>(existingCustomer || null);
  const addToast = useToast();

  const { creatorId } = useContext(teamsContext);

  const handleSearchChange = (newSearch: string): void => {
    setSearch(newSearch);
  };

  useEffect(() => {
    const modal = document.getElementById('modal-body');
    if (modal) {
      modal.scrollTo({
        behavior: 'smooth',
        top: 0,
      });
    }
  }, [selectedCustomer]);

  const handleSubmit = async (): Promise<void> => {
    if (!creatorId) {
      addToast(
        ToastTypeVariants.ERROR,
        'Unable to determine creator team. Please try refreshing the page or contact support.',
      );
      return;
    }
    if (selectedCustomer) {
      onCustomerSelect(selectedCustomer);
    }
  };

  const searchedCustomers = useMemo(
    () =>
      customers.filter(
        (customer) => (customer.name ?? '').toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1,
      ),
    [search, customers],
  );

  return (
    <div className={s.contentFluidX}>
      <Search onChange={handleSearchChange} />
      {searchedCustomers
        // move selected customer to the top of the list
        .sort((a, b) => {
          if (a.id === selectedCustomer?.id) {
            return -1;
          }
          if (b.id === selectedCustomer?.id) {
            return 1;
          }
          return 0;
        })
        .map((customer) => (
          <m.div layout='position' key={customer.id}>
            <ListCell
              tag='div'
              onClick={() => setSelectedCustomer(customer)}
              id={customer.id}
              selectedId={selectedCustomer?.id}
              avatar={{
                color: customer.color ?? '',
                name: customer.name || '',
              }}
              bottomLine={customer.email}
              label={customer.name || ''}
            />
          </m.div>
        ))}
      <Spacer size='16px' />

      <div className={s.selectCustomerButton}>
        <FetchingButton
          variant='primary'
          size='default'
          className='w-full'
          disabled={!selectedCustomer}
          fetching={isSubmitting}
          onClick={handleSubmit}
        >
          {nextButtonText}
        </FetchingButton>
      </div>
    </div>
  );
};

export default SelectCustomer;
